/**
 * Type definitions for V4 Advanced Activity Dashboard
 */

/**
 * View modes for the dashboard tabs
 */
export type ViewMode = 'timeline' | 'analytics' | 'summary' | 'export'

/**
 * Filter state structure matching original dashboard
 */
export type DashboardFilters = {
  linkedServiceID: string
  timeRange: 'week' | 'month' | 'quarter' | 'year' | 'custom'
  customStartDate: string | null
  customEndDate: string | null
  selectedUsers: string[]
  selectedCategories: string[]
  selectedLicenseTypes: string[]
  selectedGroups: string[]
  selectedActivityTypes: string[]
  viewMode: ViewMode
  showLicenseCredentialEvents: boolean // Toggle for license/credential visibility
}

export interface TimelineMetadata {
  key: string
  value: string
}

export interface TimelineAction {
  label: string
  action: string
  variant?: string
}

/**
 * CE Activity Data - Full activity data for modals
 */
export interface CEActivityData {
  activityType: string // 'ce-completion'
  certificateID: string
  userID: string
  courseId: string
  assignmentId?: string | null
  userName: string
  userEmail: string
  courseTitle: string
  courseName: string
  courseCode?: string | null
  completionDate: string
  ceHours: number
  category: string
  licenseTypes: string[]
  assignmentName?: string | null
  wasAssigned: boolean
  assignmentDueDate?: string | null
  hasCertificate: boolean
  certificateFileName?: string | null
  section?: string | null
  courseType?: string // 'Recorded', 'Live', or 'External - Recorded', 'External - Live'
}

/**
 * Assignment Event Data - Full assignment data for modals
 */
export interface AssignmentEventData {
  activityType: string // 'assignment-created'
  assignmentID: string
  assignmentName: string
  createdDate: string
  dueDate: string
  userCount: number
  courseCount: number
  totalCeHours: number
  overallCompletionPercentage: number
  assignedUserNames: string[]
  assignedCourseNames: string[]
  description?: string
}

/**
 * User Action Data - Event sourcing style immutable user actions
 */
export interface UserActionData {
  activityType: string // 'user-action'
  actionID: string
  eventID: string
  userID: string
  userName: string
  userEmail: string
  eventName: string
  actionType: string // 'event-attended' | 'event-completed'
  actionDate: string
  sessionID?: string | null
}

/**
 * License Activity Data - License tracking activities
 */
export interface LicenseActivityData {
  activityType: 'license-activity'
  licenseID: string // Backend uses uppercase ID
  userID: string // Backend uses uppercase ID
  userName: string
  userEmail: string
  licenseName: string // License type (e.g., "EMT", "Paramedic")
  licenseNumber: string
  licenseState: string // State where license is issued
  expiration?: string // ISO date string (optional for some licenses)
  actionType: string // "added" | "expiring-soon" | "expired"
  actionDate: string // ISO date string
  daysToExpiration?: number // Negative for expired, positive for active
}

/**
 * Credential Activity Data - Credential tracking activities
 */
export interface CredentialActivityData {
  activityType: 'credential-activity'
  credentialID: string // Backend uses uppercase ID
  userID: string // Backend uses uppercase ID
  userName: string
  userEmail: string
  credentialName: string // Credential type (e.g., "BLS", "ACLS", "PALS")
  credentialNumber?: string // Optional credential ID number
  expiration: string // ISO date string (required for credentials)
  actionType: string // "added" | "expiring-soon" | "expired"
  actionDate: string // ISO date string
  daysToExpiration?: number // Negative for expired, positive for active
}

/**
 * Timeline Event Data - F# Discriminated Union
 * Will be either { ceActivity: {...} } or { assignmentEvent: {...} } or { userAction: {...} }
 * or { licenseActivity: {...} } or { credentialActivity: {...} }
 */
export type TimelineEventUnion =
  | { ceActivity: CEActivityData }
  | { assignmentEvent: AssignmentEventData }
  | { userAction: UserActionData }
  | { licenseActivity: LicenseActivityData }
  | { credentialActivity: CredentialActivityData }

/**
 * Timeline Event - Core event structure from API
 */
export interface TimelineEvent {
  id: string
  title: string
  description?: string
  timestamp?: string
  type: string // 'recorded' | 'live' | 'assignment' | 'user-action'
  metadata?: TimelineMetadata[]
  actions?: TimelineAction[]
  data: TimelineEventUnion // Discriminated union from F#
}

/**
 * Timeline API Response Structure
 */
export interface TimelineResponse {
  events: TimelineEvent[]
}

/**
 * Overview/KPI data from API
 */
export type OverviewData = {
  totalUsers: number
  totalHours: number
  avgHoursPerUser: number
  activeUsers: number
  recentTrend: number
  trendLabel: string | null
}

/**
 * API query parameters for all endpoints
 */
export type DashboardQueryParams = {
  linkedServiceID: string
  startDate: string
  endDate: string
  userIDs?: string[]
  categories?: string[]
  licenseTypes?: string[]
  groupIDs?: string[]
  activityTypes?: string[]
}

/**
 * Loading/request status
 */
export type RequestStatus = 'idle' | 'loading' | 'success' | 'error'

/**
 * Summary API Response Types
 */
export interface UserEngagement {
  averageHoursPerUser: number
  totalActivities: number
  activeUserCount: number
  totalCeHours?: number // Optional since we're calculating it
}

export interface ActivityType {
  activityType: string
  count: number
  totalHours: number
  percentage: number
}

export interface AssignmentContext {
  assignedActivities: number
  assignedPercentage: number
  selfPacedActivities: number
  selfPacedPercentage: number
}

export interface TopCategory {
  categoryName: string
  totalHours: number
  activityCount: number
}

export interface TopCourse {
  courseName: string
  totalHours: number
  activityCount: number
}

export interface EventAttendance {
  totalAttendances: number
  uniqueAttendees: number
  eventsWithAttendance: number
}

export interface SummaryResponse {
  userEngagement: UserEngagement
  activityTypes: ActivityType[]
  assignmentContext: AssignmentContext
  topCategories: TopCategory[]
  topCourses: TopCourse[]
  eventAttendance: EventAttendance
}

/**
 * Type guards for discriminated union
 */
export const isCEActivity = (
  data: TimelineEventUnion,
): data is { ceActivity: CEActivityData } => 'ceActivity' in data

export const isAssignmentEvent = (
  data: TimelineEventUnion,
): data is { assignmentEvent: AssignmentEventData } => 'assignmentEvent' in data

export const isUserAction = (
  data: TimelineEventUnion,
): data is { userAction: UserActionData } => 'userAction' in data

export const isLicenseActivity = (
  data: TimelineEventUnion,
): data is { licenseActivity: LicenseActivityData } => 'licenseActivity' in data

export const isCredentialActivity = (
  data: TimelineEventUnion,
): data is { credentialActivity: CredentialActivityData } =>
  'credentialActivity' in data

/**
 * Rollup Data Item for Export functionality
 * Matches the /api/manage/dashboard/rollup endpoint response
 */
export interface RollupDataItem {
  userName: string
  email: string
  courseActivity: string
  completionDate: string
  ceHours: number
  category: string
  licenseTypes: string
  type: string
  courseCode?: string | null
  certificateID: string
  wasAssigned: boolean
  assignmentName?: string | null
  assignmentDueDate?: string | null
  eventType?: string | null
  hasCertificate: boolean
  groups: string[]
}

/**
 * Credentials Export Item for license/credential report
 * Matches the /api/manage/dashboard/credentials endpoint response
 */
export interface CredentialsExportItem {
  userName: string
  email: string
  type: 'License' | 'Credential' | 'NREMT'
  name: string // Properly formatted (e.g., "BLS", "ACLS", "NREMT-Paramedic")
  number: string
  state: string | null // State abbreviation for licenses, null for credentials and NREMT
  expiration: string | null // ISO date, null if no expiration
  status: 'active' | 'expiring-soon' | 'expired'
  daysToExpiration: number | null // Null if no expiration or already expired
  dateAdded: string // ISO date
}
